import Picker from "@/components/picker-v2";
import { Portal } from "@/components/Portal";
import { BackIcon, QaIcon } from "@/components/RFIcon";
import CircleCloseIcon from "@/components/RFIcon/icons/CircleClose";
import ConfirmModal from "@/containers/common/ConfirmModal";
import Samples from "@/containers/survey/address-proof/Samples";
import SurveyTitle from "@/containers/survey/SurverTitle";
import SurveyFooter from "@/containers/survey/SurveyFooter";
import SurveyHeader from "@/containers/survey/SurveyHeader";
import useOSS from "@/hooks/useRequest/useOSS";
import { uploadAddressProofs } from "@/hooks/useRequest/useSurveyRequest";
import { BridgeCallHandlers, log2ClientEventName, SurveyCompleteTypes } from "@/interfaces/enums";
import { AddressProofType } from "@/interfaces/surveyTypes";
import RFLayout from "@/layout/RFLayout";
import compressorFile from "@/utils/compressorFile";
import bridge from "@/utils/dsBridge";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import log2Sentry from "@/utils/log2Sentry";
import readAsDataUrl from "@/utils/readAsDataUrl";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ReactElement, useEffect, useState } from "react";
import Toast from "@/components/Toast";
import ContentContainer from "@/containers/common/ContentContainer";
import log2Client from "@/utils/log2Client";

interface FileState {
	key: number;
	path: string | ArrayBuffer | null;
	file?: File;
}

export default function AddressProofPage() {
	const { t } = useTranslation("survey");
	const { upload } = useOSS("addressProof");
	const [loading, setLoading] = useState(false);
	const [proofType, setProofType] = useState<AddressProofType>(AddressProofType.UTILITY_BILL);
	const [files, setFiles] = useState<FileState[]>([
		{ key: 1, path: "" },
		{ key: 2, path: "" },
		{ key: 3, path: "" },
	]);

	const [showSample, setShowSample] = useState(false);
	const [popVisible, setPopVisible] = useState(false);

	const ProofTypeOptions = [
		{
			label: t("utility-bills"),
			value: AddressProofType.UTILITY_BILL,
		},
		{
			label: t("government-documents"),
			value: AddressProofType.GOVERNMENT_ISSUED_LETTERS,
		},
		{
			label: t("lease-agreement"),
			value: AddressProofType.CURRENT_LEASE,
		},
		{
			label: t("bank-statement"),
			value: AddressProofType.BANK_STATEMENT,
		},
	];

	const readFile = async (_file: File, key: number) => {
		if (!_file) return;
		const compressFile = await compressorFile(_file);
		const dataUrl = await readAsDataUrl(compressFile);
		files.forEach((item) => {
			if (item.key === key) {
				item.path = dataUrl;
				item.file = compressFile;
			}
		});
		setFiles([...files]);
	};

	const handleRemove = (key: number) => {
		files.forEach((item) => {
			if (item.key === key) {
				item.path = "";
				item.file = undefined;
			}
		});
		setFiles([...files]);
	};

	const preHandleSubmit = async () => {
		if (proofType === undefined) throw Error("Proof type is required");

		const _files = files.filter((item) => item.file != undefined).map((item) => item.file as File);

		if (!_files || _files.length === 0) {
			Toast.info(t("please-upload-file"));
			return;
		}
		/* 政府文件 小于两个 文件 弹窗提醒 */
		const preValidate = proofType === AddressProofType.GOVERNMENT_ISSUED_LETTERS && _files.length < 2;

		if (preValidate) {
			setPopVisible(true);
			return;
		}

		handleSubmit();
	};

	const handleSubmit = async () => {
		if (popVisible) setPopVisible(false);

		try {
			const _files = files.filter((item) => item.file != undefined).map((item) => item.file as File);
			if (!_files || _files.length === 0) throw Error(t("please-upload-file"));

			setLoading(true);
			const images = await Promise.all(_files.map((_file) => upload(_file)));
			await uploadAddressProofs({
				proofType,
				images,
			});

			bridge.call(BridgeCallHandlers.ON_SURVEY_COMPLETE, {
				questionType: SurveyCompleteTypes.ADDRESS_PROOF,
			});
			log2Client({
				eventName: log2ClientEventName.addressProofSurveySubmitted,
			});
		} catch (error: any) {
			Toast.fail(error.message);
			log2Sentry(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.UAP_UPLOAD_NEXTPAGE);
		}
	};

	useEffect(() => {
		setTimeout(() => log2sensors(SensorsEventIds.UAP_UPLOAD_VIEW), 0);
	}, []);

	return (
		<>
			<ContentContainer className="zh-letter-space" header={<SurveyHeader hiddenBackBtn />}>
				<ContentContainer
					padding
					header={
						<SurveyTitle
							title={t("submit-a-document-to-verify-your-residential-address")}
							subNode={
								<div
									className="text-sm font-semibold"
									dangerouslySetInnerHTML={{
										__html: t("proof-of-address-needs-display-your-name-and-address-details"),
									}}
								/>
							}
						/>
					}
					footer={
						<SurveyFooter
							loading={loading}
							onNextBtnClick={preHandleSubmit}
							onPrivacyClick={() => log2sensors(SensorsEventIds.UAP_UPLOAD_PRAVICY)}
							onSecureClick={() => log2sensors(SensorsEventIds.UAP_UPLOAD_SECURITY)}
						/>
					}
				>
					<div className="pb-4">
						<div className="h-[7.75vh]"></div>

						<div className="flex items-center justify-between text-sm font-semibold">
							<div
								onClick={() => {
									setShowSample(true);
									log2sensors(SensorsEventIds.UAP_UPLOAD_CLICKFOREXAMPLES);
								}}
								className="flex items-center"
							>
								<a className="mr-1 -mt-0.5">{t("document-types")}</a>
								<QaIcon className="w-3.5 h-3.5" />
							</div>

							<Picker
								columns={[ProofTypeOptions as any]}
								defaultValue={[proofType as any]}
								onConfirm={(e) => {
									setProofType(e[0] as any);
								}}
							>
								{(items, { open }) => {
									const text = items.every((item) => item === null)
										? ""
										: items.map((item) => item?.label ?? "").join(" - ");
									return (
										<div
											onClick={() => {
												open();
												log2sensors(SensorsEventIds.UAP_UPLOAD_DOCTYPE);
											}}
											className="text-sm font-semibold flex flex-nowrap items-center border-1 border-eaeaea dark:border-252525 pl-6 pr-4 py-2.5 rounded-full"
										>
											<span className="mr-4">{text}</span>
											<div style={{ transform: "rotate(-90deg)" }}>
												<BackIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
											</div>
										</div>
									);
								}}
							</Picker>
						</div>

						<div className="mt-5 p-4 rounded-xl bg-f6f7f9 dark:bg-151515">
							<div className="flex items-center justify-between">
								{files.map((item, key) => (
									<GridView key={key}>
										{item.path ? (
											<ImgView src={item.path} item={item} handleRemove={() => handleRemove(item.key)} />
										) : (
											<UploadView onChange={(file) => readFile(file, item.key)} />
										)}
									</GridView>
								))}
							</div>
						</div>

						<p className="mt-6 text-xs font-semibold">{t("supported-document-types")}</p>
					</div>

					<ConfirmModal
						visible={popVisible}
						onClose={() => setPopVisible(false)}
						title={t("document-confirm-title")}
						onCancelText={t("cancel", { ns: "common" })}
						onCancel={() => setPopVisible(false)}
						onOkText={t("confirm", { ns: "common" })}
						onOk={handleSubmit}
					>
						<div>{t("document-confirm-desc")}</div>
					</ConfirmModal>

					{showSample && (
						<Portal>
							<Samples type={proofType} onCloseClick={() => setShowSample(false)} />
						</Portal>
					)}
				</ContentContainer>
			</ContentContainer>
		</>
	);
}

function GridView({ children }: { children: React.ReactNode }) {
	return (
		<div>
			<div
				className="w-[20vw] h-[20vw] relative 
        max-h-[90px] max-w-[90px] 
        bg-no-repeat bg-center bg-cover 
        bg-[url('https://web-cdn.rockflow.tech/h5/deposit/credentials-light.png')]
        dark:bg-[url('https://web-cdn.rockflow.tech/h5/deposit/credentials-dark.png')]"
			>
				{children}
			</div>
		</div>
	);
}

function UploadView({ onChange }: { onChange: (file: File) => void }) {
	return (
		<div className="flex h-full w-full items-center justify-center">
			<input
				type="file"
				accept="image/*"
				onChange={(e) => {
					const _file = e.target.files?.[0] as File;
					if (!_file) return;
					onChange(_file);
					e.target.value = "";
				}}
				className="absolute left-0 top-0 opacity-0 w-full h-full"
			/>
		</div>
	);
}

function ImgView({ src, handleRemove }: { src: string | ArrayBuffer; item: FileState; handleRemove: () => void }) {
	return (
		<>
			<div className="p-0.5 w-full h-full">
				<div className="w-full h-full p-0.5">
					<img src={src as any} alt="file" className="block w-full h-full object-contain" />
				</div>
			</div>

			<span className="rounded-full dark:bg-black absolute -top-1 -right-1 bg-white w-4 h-4">
				<CircleCloseIcon className="w-5 h-5 link-text" onClick={handleRemove} />
			</span>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async ({ locale = "en" }) => {
	return {
		props: {
			...(await serverSideTranslations(locale, ["common", "survey", "error"])),
		},
	};
};

AddressProofPage.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
